import React, { useState } from 'react';
import {
  FileText, Receipt, Users, Wallet, Tag, Unplug, Database, Download, Table,
} from 'lucide-react';
import { getApiBaseUrl } from '../config/api';
import '../styles/analytics-theme.css';

const REPORT_DATASETS = [
  {
    id: 'transactions',
    title: 'All Transactions',
    description: 'Agent-linked covers from agent_business (operational list; dashboard production uses completed payments).',
    icon: Receipt,
    accent: 'sky',
    endpoint: '/api/admin/transactions?limit=10000',
    note: 'Use the All Transactions tab for interactive filtering. Export pulls up to 10,000 rows.',
  },
  {
    id: 'agents',
    title: 'Agent Transactions',
    description: 'Commission-bearing business transactions with gross, WHT, and net commission.',
    icon: Users,
    accent: 'emerald',
    endpoint: '/api/admin/analytics/agent-transactions?exportAll=true',
  },
  {
    id: 'commissions',
    title: 'Commissions Payable',
    description: 'Outstanding and paid commission records for finance reconciliation.',
    icon: Wallet,
    accent: 'violet',
    endpoint: '/api/admin/commissions?limit=10000',
  },
  {
    id: 'product-rates',
    title: 'Product Rates',
    description: 'Active and inactive TPO/TOR rate schedule with net and gross amounts.',
    icon: Tag,
    accent: 'amber',
    endpoint: '/api/admin/product-rates',
  },
  {
    id: 'unsynchronized',
    title: 'Unsynchronized Policies',
    description: 'Issued certificates pending core submission reconciliation.',
    icon: Unplug,
    accent: 'rose',
    endpoint: '/api/admin/unsynchronized-policies?exportAll=true',
  },
];

const ACCENT = {
  sky: { bg: '#e0f2fe', color: '#0284c7', border: '#0ea5e9' },
  emerald: { bg: '#d1fae5', color: '#059669', border: '#10b981' },
  violet: { bg: '#ede9fe', color: '#7c3aed', border: '#8b5cf6' },
  amber: { bg: '#fef3c7', color: '#d97706', border: '#f59e0b' },
  rose: { bg: '#ffe4e6', color: '#e11d48', border: '#f43f5e' },
};

function flattenForCsv(data, datasetId) {
  if (datasetId === 'product-rates' && data.rates) return data.rates;
  if (data.data?.rows) return data.data.rows;
  if (data.rows) return data.rows;
  if (data.commissions) return data.commissions;
  if (data.rates) return data.rates;
  if (Array.isArray(data.data)) return data.data;
  if (Array.isArray(data)) return data;
  return [];
}

function toCsv(rows) {
  if (!rows.length) return '';
  const keys = Object.keys(rows[0]);
  const header = keys.join(',');
  const body = rows.map((row) =>
    keys.map((k) => {
      const v = row[k];
      const s = v == null ? '' : String(v);
      return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
    }).join(',')
  );
  return [header, ...body].join('\n');
}

function downloadBlob(content, filename, type) {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

const AdminReports = ({ onShowNotification }) => {
  const [selectedId, setSelectedId] = useState(REPORT_DATASETS[0].id);
  const [exporting, setExporting] = useState(false);
  const [previewCount, setPreviewCount] = useState(null);

  const selected = REPORT_DATASETS.find((r) => r.id === selectedId) || REPORT_DATASETS[0];
  const accent = ACCENT[selected.accent] || ACCENT.sky;

  const loadData = async () => {
    const token = localStorage.getItem('adminToken');
    const response = await fetch(`${getApiBaseUrl()}${selected.endpoint}`, {
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error || 'Failed to load report data');
    }
    return response.json();
  };

  const handlePreview = async () => {
    try {
      const data = await loadData();
      const rows = flattenForCsv(data, selected.id);
      setPreviewCount(rows.length);
      if (onShowNotification) onShowNotification(`${rows.length.toLocaleString()} records match this dataset.`, 'info');
    } catch (error) {
      if (onShowNotification) onShowNotification(error.message, 'error');
    }
  };

  const handleExport = async (format) => {
    setExporting(true);
    try {
      const data = await loadData();
      const rows = flattenForCsv(data, selected.id);
      if (!rows.length) {
        if (onShowNotification) onShowNotification('No records to export.', 'warning');
        return;
      }
      const stem = `monarch-${selected.id}-${new Date().toISOString().slice(0, 10)}`;
      if (format === 'csv') {
        downloadBlob(toCsv(rows), `${stem}.csv`, 'text/csv');
      } else {
        const XLSX = await import('xlsx');
        const ws = XLSX.utils.json_to_sheet(rows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Report');
        XLSX.writeFile(wb, `${stem}.xlsx`);
      }
      if (onShowNotification) onShowNotification(`Exported ${rows.length.toLocaleString()} rows.`, 'success');
    } catch (error) {
      if (onShowNotification) onShowNotification(error.message, 'error');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="ab-page">
      <div className="ab-page-header">
        <div className="ab-header-with-icon">
          <div className="ab-page-icon-wrap"><FileText size={20} /></div>
          <div>
            <h1 className="ab-page-title">Reports</h1>
            <p className="ab-page-subtitle">Build a snapshot of any module and download as CSV or Excel.</p>
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(260px, 1fr) 2fr', gap: '1.5rem' }}>
        <div>
          <h2 className="ab-stat-label" style={{ marginBottom: '0.75rem', paddingLeft: '0.25rem' }}>Choose a dataset</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {REPORT_DATASETS.map((report) => {
              const Icon = report.icon;
              const a = ACCENT[report.accent] || ACCENT.sky;
              const active = selectedId === report.id;
              return (
                <button
                  key={report.id}
                  type="button"
                  onClick={() => { setSelectedId(report.id); setPreviewCount(null); }}
                  className="ab-card"
                  style={{
                    textAlign: 'left',
                    cursor: 'pointer',
                    padding: '1rem',
                    border: active ? `2px solid ${a.border}` : undefined,
                    boxShadow: active ? `0 0 0 3px ${a.bg}` : undefined,
                    background: 'var(--ab-card)',
                    width: '100%',
                  }}
                >
                  <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
                    <div style={{ width: 44, height: 44, borderRadius: 8, background: a.bg, color: a.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Icon size={20} />
                    </div>
                    <div>
                      <h3 style={{ margin: 0, fontSize: '0.875rem', fontWeight: 600 }}>{report.title}</h3>
                      <p style={{ margin: '0.25rem 0 0', fontSize: '0.75rem', color: 'var(--ab-muted-fg)', lineHeight: 1.4 }}>{report.description}</p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="ab-card">
          <div className="ab-card-header">
            <h3 className="ab-card-title">Export — {selected.title}</h3>
          </div>
          <div className="ab-card-body">
            <p style={{ fontSize: '0.8125rem', color: 'var(--ab-muted-fg)', marginTop: 0 }}>{selected.description}</p>
            {selected.note && (
              <p style={{ fontSize: '0.75rem', color: 'var(--ab-muted-fg)', fontStyle: 'italic' }}>{selected.note}</p>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0.75rem', margin: '1.25rem 0' }}>
              <div className="ab-accent-card sky" style={{ borderLeftWidth: 3 }}>
                <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                  <Database size={20} style={{ color: accent.color }} />
                  <div>
                    <p className="ab-stat-label">Records</p>
                    <p className="ab-stat-value" style={{ fontSize: '1.25rem' }}>{previewCount != null ? previewCount.toLocaleString() : '—'}</p>
                  </div>
                </div>
              </div>
            </div>

            <div style={{ border: '1px dashed var(--ab-border)', borderRadius: 8, padding: '1rem', background: 'var(--ab-muted)' }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
                  <Download size={16} style={{ marginTop: 2, color: 'var(--ab-muted-fg)' }} />
                  <div>
                    <p style={{ margin: 0, fontSize: '0.875rem', fontWeight: 500 }}>Generate {selected.title} report</p>
                    <p style={{ margin: '0.25rem 0 0', fontSize: '0.75rem', color: 'var(--ab-muted-fg)' }}>Preview count, then download CSV or Excel.</p>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <button className="ab-btn sm" onClick={handlePreview} disabled={exporting}>Preview count</button>
                  <button className="ab-btn sm" onClick={() => handleExport('csv')} disabled={exporting}>
                    <FileText size={14} /> CSV
                  </button>
                  <button className="ab-btn sm primary" onClick={() => handleExport('excel')} disabled={exporting}>
                    <Table size={14} /> Excel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminReports;
